import {
  mapIdToCalendarDate,
  mapIdToVariant,
  mapOgUrl,
  type MapVariant,
} from "@dailywars/core/engine";

const APP_NAME = "Daily Wars";
const SEPARATOR = " | ";
export const SITE_ORIGIN = "https://playdailywars.com";
const OG_IMAGE_URL = `${SITE_ORIGIN}/og-image.png`;
const DEFAULT_DESCRIPTION =
  "Your daily tactical puzzle. Build an army and destroy the AI as fast as you can. New maps every day at midnight UTC.";

type DocumentHeadOptions = {
  description?: string;
  canonicalPath?: string;
  imageUrl?: string;
};

function documentTitle(page: string): string {
  return `${APP_NAME}${SEPARATOR}${page}`;
}

export const DOCUMENT_TITLES = {
  home: documentTitle("Daily turn-based tactical game"),
  codex: documentTitle("Game Guide"),
  tutorial: documentTitle("Tutorial"),
  profile: documentTitle("Profile"),
  achievements: documentTitle("Achievements"),
  archive: documentTitle("Archive"),
  stats: documentTitle("Global Stats"),
  hiscores: documentTitle("Hiscores"),
  terms: documentTitle("Terms of Service"),
  privacyPolicy: documentTitle("Privacy Policy"),
  cookiePolicy: documentTitle("Cookie Policy"),
  premium: documentTitle("Upgrade to Premium"),
  updates: documentTitle("Updates"),
  replay: documentTitle("Replay"),
} as const;

export function documentHead(title: string, options: DocumentHeadOptions = {}) {
  const description = options.description ?? DEFAULT_DESCRIPTION;
  const imageUrl = options.imageUrl ?? OG_IMAGE_URL;
  const meta = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:site_name", content: APP_NAME },
    { property: "og:description", content: description },
    { property: "og:image", content: imageUrl },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: imageUrl },
  ];

  const links: Array<{ rel: string; href: string }> = [];
  if (options.canonicalPath) {
    const canonicalUrl = `${SITE_ORIGIN}${options.canonicalPath}`;
    meta.push({ property: "og:url", content: canonicalUrl });
    links.push({ rel: "canonical", href: canonicalUrl });
  }

  return { meta, ...(links.length > 0 ? { links } : {}) };
}

function mapVariantLabel(variant: MapVariant): string {
  return variant === "premium" ? "Premium" : "Standard";
}

export function playMapTitle(mapId: string): string {
  const mapDate = mapIdToCalendarDate(mapId);
  const variant = mapVariantLabel(mapIdToVariant(mapId));
  return documentTitle(`${mapDate} ${variant}`);
}

export function playMapDescription(mapId: string): string {
  const mapDate = mapIdToCalendarDate(mapId);
  const variant = mapVariantLabel(mapIdToVariant(mapId)).toLowerCase();
  return `Play the Daily Wars ${variant} map for ${mapDate}. Turn-based tactical skirmish against the AI.`;
}

export function leaderboardMapTitle(mapId: string): string {
  const mapDate = mapIdToCalendarDate(mapId);
  const variant = mapVariantLabel(mapIdToVariant(mapId));
  return documentTitle(`Leaderboard, ${mapDate} ${variant}`);
}

export function leaderboardMapDescription(mapId: string): string {
  const mapDate = mapIdToCalendarDate(mapId);
  const variant = mapVariantLabel(mapIdToVariant(mapId)).toLowerCase();
  return `See who cleared the ${mapDate} ${variant} map in the fewest days.`;
}

export function mapOgImageUrl(mapId: string): string {
  return `${SITE_ORIGIN}${mapOgUrl(mapId)}`;
}
